import type { ComponentProps, ReactElement, ReactNode, Ref } from "react"
import type { useValidatedForm } from "#components/form/useValidatedForm"
import type { Option } from "#components/option"
import type { FieldValues } from "react-hook-form"
import type { z } from "zod"

import React, { createContext, useContext, useRef } from "react"
import { FormProvider } from "react-hook-form"
import { ZodObject, ZodType } from "zod"

import { setDataTouched } from "#components/form/useValidatedForm"
import { MultiStepProgress } from "#components/lib/layout/multiStepProgress/multiStepProgress"

const ValidationContext = createContext<{ validationSchema?: z.Schema<any, any> }>({})

export function RHFFormContext<
  Schema extends z.Schema<any, any>,
  InputFieldValues extends FieldValues,
  OutputFieldValues extends FieldValues,
  InputFieldValuesProps extends InputFieldValues = InputFieldValues,
>({ formMethods, children }: { formMethods: ReturnType<typeof useValidatedForm<Schema, InputFieldValues, OutputFieldValues, InputFieldValuesProps>>; children: ReactNode }) {
  return (
    // @ts-ignore künstlich das validationSchema mit zum Kontext hinzufügen
    <FormProvider {...formMethods} handleSubmit={undefined as any}>
      <ValidationContext.Provider value={{ validationSchema: formMethods.validationSchema }}>{children}</ValidationContext.Provider>
    </FormProvider>
  )
}

export function RHFFormElement<
  Schema extends z.Schema<any, any>,
  InputFieldValues extends FieldValues,
  OutputFieldValues extends FieldValues,
  InputFieldValuesProps extends InputFieldValues = InputFieldValues,
>({
  formMethods,
  children,
  formRef,
  formSubmitKey,
  ...restFormProps
}: { formRef?: Ref<HTMLFormElement>; formSubmitKey?: string } & ComponentProps<typeof RHFFormContext<Schema, InputFieldValues, OutputFieldValues, InputFieldValuesProps>> &
  Omit<ComponentProps<"form">, "onSubmit" | "children">) {
  return (
    <form {...restFormProps} ref={formRef} onSubmit={formMethods.onSubmit} data-formid={formSubmitKey}>
      {children}
    </form>
  )
}

export function RHFForm<
  Schema extends z.Schema<any, any>,
  InputFieldValues extends FieldValues,
  OutputFieldValues extends FieldValues,
  InputFieldValuesProps extends InputFieldValues = InputFieldValues,
>(
  props: { formRef?: Ref<HTMLFormElement>; formSubmitKey?: string } & ComponentProps<typeof RHFFormContext<Schema, InputFieldValues, OutputFieldValues, InputFieldValuesProps>> &
    Omit<ComponentProps<"form">, "onSubmit" | "children">,
) {
  return (
    <RHFFormContext formMethods={props.formMethods}>
      <RHFFormElement {...props}>{props.children}</RHFFormElement>
    </RHFFormContext>
  )
}

/**
 * A Multi Step form, based on react hook forms, it takes <Option> elements as children - each option represents a view with inputs, on each submit the key with the corresponding index in the validation zod object is validated - so each input fields in an option should correspond to the correct zod key - see verification/page as example
 * @param shouldCheckFormValidity only checks RHF Form validity if function returns true for step
 */
export function MultiStepRHFForm<
  TControlAppearance extends object,
  Schema extends z.ZodObject<any, any, any, any, any>,
  InputFieldValues extends FieldValues,
  OutputFieldValues extends FieldValues,
  InputFieldValuesProps extends InputFieldValues = InputFieldValues,
>({
  children,
  multiStepOptions,
  shouldCheckFormValidity,
  ...restRHFFormProps
}: Omit<ComponentProps<typeof RHFForm<Schema, InputFieldValues, OutputFieldValues, InputFieldValuesProps>>, "formSubmitKey"> & {
  shouldCheckFormValidity?: (step: number) => boolean
  multiStepOptions: Omit<ComponentProps<typeof MultiStepProgress<TControlAppearance>>, "children">
  children: ReactElement<ComponentProps<typeof Option>>[]
}) {
  multiStepOptions.formSubmitKey ??= "multiStepForm"

  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmitStep = async (step: number) => {
    if (!shouldCheckFormValidity || shouldCheckFormValidity(step)) {
      const name = Object.keys(restRHFFormProps.formMethods.validationSchema.shape)[step - 1] as any
      const valid = await restRHFFormProps.formMethods.trigger(name, { shouldFocus: true })

      // Set touched state for inputfield components to enable checkmark detection
      setDataTouched([...formRef.current!.elements] as HTMLElement[], "true", name)
      // Set state for each of the inputs components to dirty and touched to enable instant revalidation
      restRHFFormProps.formMethods.setValue(name, restRHFFormProps.formMethods.getValues(name), { shouldTouch: true, shouldDirty: true })

      if (!valid) return false
    }

    if (multiStepOptions.onSubmitStep) return multiStepOptions.onSubmitStep(step)

    return true
  }

  // Add dispatch of custom event to let MultiStepProgress and Controls know that form submission handling has finished
  const originalSubmit = restRHFFormProps.formMethods.onSubmit
  restRHFFormProps.formMethods.onSubmit = async (e?: React.BaseSyntheticEvent) => {
    await originalSubmit(e)
    window.dispatchEvent(new Event(`formSubmit-${multiStepOptions.formSubmitKey}`))
  }

  return (
    <RHFForm {...restRHFFormProps} formRef={formRef} formSubmitKey={multiStepOptions.formSubmitKey}>
      <MultiStepProgress {...multiStepOptions} onSubmitStep={handleSubmitStep}>
        {children}
      </MultiStepProgress>
    </RHFForm>
  )
}

export function useValidationContext() {
  return useContext(ValidationContext)
}
